import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["fileInput"]
  static values = {
    mapContainerId: { type: String, default: 'map_container' },
    importContainerId: { type: String, default: 'import_container' }
  }

  connect() {
    this.setupFileInputListener()
  }
  
  toggleMethod(event) {
    const method = event.target.value
    const mapContainer = document.getElementById(this.mapContainerIdValue)
    const importContainer = document.getElementById(this.importContainerIdValue)

    if (method === 'draw') {
      mapContainer.classList.remove('d-none')
      importContainer.classList.add('d-none')
    } else if (method === 'import') {
      mapContainer.classList.add('d-none')
      importContainer.classList.remove('d-none')
    }
  }

  setupFileInputListener() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          const fileItems = document.querySelectorAll('.file-item:not(.existing-file)')
          if (fileItems.length > 0) {
            this.checkForNewFiles()
          }
        }
      })
    })

    const fileListWrapper = this.element.closest('form').querySelector('.file-list-wrapper')
    if (fileListWrapper) {
      observer.observe(fileListWrapper, { childList: true, subtree: true })
    }
  }

  checkForNewFiles() {
    if (this.fileInputTarget && this.fileInputTarget.files && this.fileInputTarget.files.length > 0) {
      this.importGeoJSON()
    }
  }

  importGeoJSON() {
    const file = this.fileInputTarget.files[0]
    if (!file) return

    const reader = new FileReader()
    
    reader.onload = (e) => {
      try {
        const geojsonData = JSON.parse(e.target.result)
        this.processGeoJSON(geojsonData)
      } catch (error) {
        console.error('Erro ao processar o arquivo GeoJSON:', error)
      }
    }
    
    reader.onerror = () => {
      console.error('Erro ao ler o arquivo')
    }
    
    reader.readAsText(file)
  }

  processGeoJSON(geojsonData) {
    if (!this.isValidGeoJSON(geojsonData)) {
      console.error('O arquivo não contém um GeoJSON válido')
      return
    }

    const geometryField = document.querySelector('input[name="route[geometry]"]')
    if (!geometryField) {
      console.error('Campo de geometria não encontrado')
      return
    }

    const processedGeoJSON = this.ensureFeatureCollection(geojsonData)
    
    const finalGeoJSON = this.addStartEndMarkers(processedGeoJSON)
    
    geometryField.value = JSON.stringify(finalGeoJSON)
    
    this.updateMapIfPossible(finalGeoJSON)
  }

  isValidGeoJSON(data) {
    if (!data) return false
    
    if (data.type === 'FeatureCollection' && Array.isArray(data.features)) {
      return true
    }
    
    if (data.type === 'Feature' && data.geometry) {
      return true
    }
    
    if (['Point', 'LineString', 'Polygon', 'MultiPoint', 'MultiLineString', 'MultiPolygon'].includes(data.type)) {
      return true
    }
    
    return false
  }

  ensureFeatureCollection(geojsonData) {
    if (geojsonData.type === 'FeatureCollection' && Array.isArray(geojsonData.features)) {
      return geojsonData
    }
    
    if (geojsonData.type === 'Feature') {
      return {
        type: 'FeatureCollection',
        features: [geojsonData]
      }
    }
    
    if (['Point', 'LineString', 'Polygon', 'MultiPoint', 'MultiLineString', 'MultiPolygon'].includes(geojsonData.type)) {
      return {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: geojsonData,
          properties: {}
        }]
      }
    }
    
    console.error('Formato GeoJSON não reconhecido')
    return {
      type: 'FeatureCollection',
      features: []
    }
  }

  addStartEndMarkers(geojsonData) {
    const result = JSON.parse(JSON.stringify(geojsonData))
    
    for (const feature of result.features) {
      if (feature.geometry && feature.geometry.type === 'LineString' && 
          Array.isArray(feature.geometry.coordinates) && 
          feature.geometry.coordinates.length >= 2) {
        
        const startCoords = feature.geometry.coordinates[0]
        const endCoords = feature.geometry.coordinates[feature.geometry.coordinates.length - 1]
        
        const hasStartMarker = result.features.some(f => 
          f.properties && f.properties.markerType === 'start' && f.properties.isRoutePoint
        )
        
        const hasEndMarker = result.features.some(f => 
          f.properties && f.properties.markerType === 'end' && f.properties.isRoutePoint
        )
        
        if (!hasStartMarker) {
          result.features.push({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: startCoords
            },
            properties: {
              title: 'Largada',
              markerType: 'start',
              description: 'Ponto inicial da rota',
              isRoutePoint: true
            }
          })
        }
        
        // Adicionar marcador de fim se não existir
        if (!hasEndMarker) {
          result.features.push({
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: endCoords
            },
            properties: {
              title: 'Chegada',
              markerType: 'end',
              description: 'Ponto final da rota',
              isRoutePoint: true
            }
          })
        }
        
        // Só precisamos processar a primeira LineString encontrada
        break
      }
    }
    
    return result
  }

  updateMapIfPossible(geojsonData) {
    // Tentar encontrar o controlador do mapa
    const mapElement = document.querySelector('[data-controller="leaflet-map"]')
    if (mapElement) {
      // Verificar se o controlador Stimulus do mapa está disponível
      const mapController = this.application.getControllerForElementAndIdentifier(mapElement, 'leaflet-map')
      if (mapController && typeof mapController.loadGeoJSON === 'function') {
        // Se o controlador tem um método loadGeoJSON, usar para carregar os dados
        mapController.loadGeoJSON(geojsonData)
      }
    }
  }
}
